import { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import QRCodeStyling, { Options } from 'qr-code-styling';

import ethSvg from '@frontend/assets/tickers/eth.svg';
import btcSvg from '@frontend/assets/tickers/btc.svg';
import dashSvg from '@frontend/assets/tickers/dash.svg';
import egldSvg from '@frontend/assets/tickers/egld.svg';
import usdcSvg from '@frontend/assets/tickers/usdc.svg';
import usdtSvg from '@frontend/assets/tickers/usdt.svg';
import utkSvg from '@frontend/assets/tickers/utk.svg';
import binanceSvg from '@frontend/assets/exchanges/binance.svg';
import polSvg from '@frontend/assets/tickers/pol.svg';
import suiSvg from '@frontend/assets/tickers/sui.svg';
import solSvg from '@frontend/assets/tickers/sol.svg';
import bnbSvg from '@frontend/assets/tickers/bnb.svg';
import styles from './QRCode.module.css';
import { ApiCurrencySymbolKeys } from '../../utils';

type Currency = ApiCurrencySymbolKeys | 'BINANCE';

interface QRCodeProps {
  className?: string;
  data: string;
  currency: Currency;
  styles?: React.CSSProperties;
}

const mapCurrencyToImage: Partial<Record<Currency, string>> = {
  BTC: btcSvg,
  ETH: ethSvg,
  DASH: dashSvg,
  EGLD: egldSvg,
  USDC: usdcSvg,
  USDT: usdtSvg,
  UTK: utkSvg,
  POL: polSvg,
  SUI: suiSvg,
  SOL: solSvg,
  BNB: bnbSvg,
  BINANCE: binanceSvg,
};

function getOptions(data: string, currency: Currency): Partial<Options> {
  const foregroundColor = '#252d3d';
  const backgroundColor = '#ffffff';

  return {
    data,
    margin: 0,
    qrOptions: {
      typeNumber: 0,
      mode: 'Byte',
      errorCorrectionLevel: 'Q',
    },
    imageOptions: {
      hideBackgroundDots: true,
      imageSize: 0.4,
      margin: 5,
    },
    dotsOptions: {
      type: 'rounded',
      color: foregroundColor,
    },
    cornersSquareOptions: {
      type: 'extra-rounded',
      color: foregroundColor,
    },
    cornersDotOptions: {
      type: undefined,
      color: foregroundColor,
    },
    backgroundOptions: {
      color: backgroundColor,
    },
    image: mapCurrencyToImage[currency],
  };
}

export default function QRCode({
  className,
  data,
  currency,
  ...props
}: QRCodeProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [qrCode] = useState<QRCodeStyling>(
    new QRCodeStyling(getOptions(data, currency))
  );

  useEffect(() => {
    if (ref.current) {
      qrCode.append(ref.current);
    }
  }, [qrCode, ref]);

  return (
    <div
      className={cx(styles['base'], className)}
      ref={ref}
      data-test-id="qr-code"
      style={props.styles}
    />
  );
}
